import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { MagnifyingGlass, Eye, ArrowCounterClockwise, Wrench } from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { API, AXIOS_SKIP_HTTP_CACHE, ORDERS_LIST_PARAMS } from '../config';
import { isAdmin } from '../utils/roles';
import { orderMatchesGlobalSearch } from '../utils/listSearch';
import {
  countServicebonArchiveRounds,
  getClosedOrderCompletedTime,
  hasUnreadWorkerNotes,
} from '../utils/orderAttention';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../components/ui/pagination';

/** Street on first line, postcode (often incl. city) on second — matches Offers address column layout. */
function formatClosedOrderAddress(order) {
  const street = String(order?.address ?? '').trim();
  const postcode = String(order?.postcode ?? '').trim();
  if (!street && !postcode) return null;
  if (!postcode) return { line1: street, line2: null };
  if (!street) return { line1: postcode, line2: null };
  return { line1: `${street},`, line2: postcode };
}

const ClosedOrdersPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const canReopen = isAdmin(user);
  const [orders, setOrders] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);

  const loadData = useCallback(async () => {
    try {
      const [ordersRes, teamsRes] = await Promise.all([
        axios.get(`${API}/orders`, { withCredentials: true, ...AXIOS_SKIP_HTTP_CACHE, ...ORDERS_LIST_PARAMS }),
        axios.get(`${API}/teams`, { withCredentials: true }),
      ]);
      setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : []);
      setTeams(Array.isArray(teamsRes.data) ? teamsRes.data : []);
    } catch (error) {
      toast.error(t('failedFetchData'));
    }
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadData();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadData]);

  const getTeamName = (teamId) => {
    const team = teams.find((t2) => String(t2.id) === String(teamId));
    return team?.name || '-';
  };

  const closedOrders = useMemo(() => {
    const list = orders.filter(
      (o) => String(o.mobile_work_status || '') === 'completed' && !o.pending_closed_release,
    );
    return [...list].sort(
      (a, b) => getClosedOrderCompletedTime(b) - getClosedOrderCompletedTime(a),
    );
  }, [orders]);

  const filteredOrders = useMemo(
    () => closedOrders.filter((o) => orderMatchesGlobalSearch(o, searchQuery)),
    [closedOrders, searchQuery]
  );

  useEffect(() => {
    setPage(1);
  }, [searchQuery, pageSize]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredOrders.length / pageSize));
  }, [filteredOrders.length, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pagedOrders = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredOrders.slice(start, start + pageSize);
  }, [filteredOrders, page, pageSize]);

  const formatCompletedAt = (order) => {
    const raw = order?.client_signature_json?.completed_at;
    if (!raw) return '-';
    const dt = new Date(raw);
    if (Number.isNaN(dt.getTime())) return '-';
    return dt.toLocaleString(language === 'en' ? 'en-GB' : 'nl-NL');
  };

  const handleView = (orderId, tab = null) => {
    const params = new URLSearchParams({ view: String(orderId), from: 'closed' });
    if (tab) params.set('tab', tab);
    navigate(`/orders?${params.toString()}`);
  };

  const handleReopen = async (order) => {
    if (!window.confirm(t('reopenOrderConfirm'))) return;
    try {
      const { data } = await axios.put(
        `${API}/orders/${order.id}/reopen-completion`,
        {},
        { withCredentials: true, ...AXIOS_SKIP_HTTP_CACHE },
      );
      if (data && data.id != null) {
        setOrders((prev) => prev.map((o) => (String(o.id) === String(data.id) ? { ...o, ...data } : o)));
      }
      toast.success(t('reopenOrderSuccess'));
      if (data?.id != null) {
        navigate(`/orders?view=${data.id}&from=service&tab=servicebon`);
      } else {
        navigate('/service');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : t('reopenOrderFailed'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00CBA9] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="closed-orders-page" className="animate-fadeIn">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="page-title">{t('closedOrders')}</h1>
          <p className="page-subtitle">{filteredOrders.length} {t('total')}</p>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-6">
        <div className="relative min-w-0 flex-1 max-w-md">
          <MagnifyingGlass
            size={18}
            className="pointer-events-none absolute left-3 top-1/2 z-[1] -translate-y-1/2 text-[#94a3b8]"
            aria-hidden
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('searchOrders')}
            className="vmk-input !pl-[2.75rem] !pr-4"
          />
        </div>
        {filteredOrders.length > 25 && (
          <div className="w-[170px]">
            <Select
              value={String(pageSize)}
              onValueChange={(v) => setPageSize(Number(v) || 25)}
            >
              <SelectTrigger className="vmk-input h-11 border-[#e2e8f0] bg-white pl-3 pr-2">
                <span className="text-xs font-semibold text-[#64748b] mr-2">
                  {t('rowsPerPage')}
                </span>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="table-container">
        <table className="vmk-table">
          <thead>
            <tr>
              <th>{t('client')}</th>
              <th>{t('address')}</th>
              <th>{t('team')}</th>
              <th>{t('offerNumber')}</th>
              <th>{t('status')}</th>
              <th>{t('completedAt')}</th>
              <th>{t('serviceRoundsColumn')}</th>
              <th className="text-right">{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {pagedOrders.map((order) => (
              <tr key={order.id}>
                <td>
                  <span className="inline-flex items-center gap-1.5 flex-wrap">
                    <span>{order.client_name || '-'}</span>
                    {hasUnreadWorkerNotes(order) ? (
                      <span
                        className="orders-list-attention-dot"
                        title={t('tabNotifyWorkerNote')}
                        aria-label={t('tabNotifyWorkerNote')}
                        role="img"
                      />
                    ) : null}
                  </span>
                </td>
                <td className="align-top whitespace-pre-line">
                  {(() => {
                    const addr = formatClosedOrderAddress(order);
                    if (!addr) return '-';
                    return (
                      <>
                        <div className="text-sm text-[#1a1a1a] dark:text-[#f1f5f9]">{addr.line1}</div>
                        {addr.line2 ? (
                          <div className="text-xs text-[#94a3b8]">{addr.line2}</div>
                        ) : null}
                      </>
                    );
                  })()}
                </td>
                <td>{getTeamName(order.assigned_team_id)}</td>
                <td>{order.offer_id || '-'}</td>
                <td>
                  <span className="status-badge completed">{t('statusCompleted')}</span>
                </td>
                <td>{formatCompletedAt(order)}</td>
                <td>
                  {countServicebonArchiveRounds(order) > 0 ? (
                    <span className="text-sm text-[#64748b]">
                      {t('serviceRoundCount', countServicebonArchiveRounds(order))}
                    </span>
                  ) : (
                    '-'
                  )}
                </td>
                <td className="table-actions-cell">
                  <div className="table-row-actions">
                    <button
                      type="button"
                      onClick={() => handleView(order.id)}
                      className="vmk-btn vmk-btn-icon"
                      title={t('historyView')}
                      aria-label={t('historyView')}
                    >
                      <Eye size={16} weight="bold" />
                    </button>
                    {countServicebonArchiveRounds(order) > 0 ? (
                      <button
                        type="button"
                        onClick={() => handleView(order.id, 'servicebon')}
                        className="vmk-btn vmk-btn-icon"
                        title={t('serviceOpenServicebon')}
                        aria-label={t('serviceOpenServicebon')}
                      >
                        <Wrench size={16} weight="bold" />
                      </button>
                    ) : null}
                    {canReopen ? (
                      <button
                        type="button"
                        onClick={() => void handleReopen(order)}
                        className="vmk-btn vmk-btn-icon vmk-btn-icon-warn"
                        title={t('historyRevoke')}
                        aria-label={t('historyRevoke')}
                      >
                        <ArrowCounterClockwise size={16} weight="bold" />
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredOrders.length > pageSize && (
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-[#64748b]">
            {t('page')} {page} / {totalPages}
          </p>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setPage((p) => Math.max(1, p - 1));
                  }}
                  className={page <= 1 ? 'pointer-events-none opacity-50' : ''}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  isActive
                  onClick={(e) => e.preventDefault()}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setPage((p) => Math.min(totalPages, p + 1));
                  }}
                  className={page >= totalPages ? 'pointer-events-none opacity-50' : ''}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default ClosedOrdersPage;
